import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import isObject from 'lodash-es/isObject';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { defineMessage, useIntl } from 'react-intl';
import { loadPackage, isMessage } from '@panneau/core/utils';
import TextField from '@panneau/field-text';
import 'react-datepicker/dist/react-datepicker.css';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';

var definition = [{
  id: 'date-time',
  component: 'DateTime'
}, {
  id: 'date',
  component: 'Date'
}, {
  id: 'time',
  component: 'Time'
}];

var styles = {"container":"panneau-field-date-container","popper":"panneau-field-date-popper"};

// This package has fucked exports, no default
var FinalDatePicker = isObject(DatePicker) && DatePicker["default"] ? DatePicker["default"] : DatePicker;
var propTypes$2 = {
  name: PropTypes.string,
  value: PropTypes.string,
  errors: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types
  size: PropTypes.oneOf(['sm', 'lg']),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  format: PropTypes.string,
  // The actual date format, see https://date-fns.org/v2.22.1/docs/format
  dateFormat: PropTypes.string,
  // Format for react-datepicker
  withoutDate: PropTypes.bool,
  // Format for react-datepicker
  withoutTime: PropTypes.bool,
  timeFormat: PropTypes.string,
  timeCaption: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types
  timeIntervals: PropTypes.number,
  className: PropTypes.string,
  onChange: PropTypes.func
};
var defaultProps$2 = {
  name: null,
  value: null,
  errors: null,
  size: null,
  required: false,
  disabled: false,
  placeholder: null,
  format: null,
  withoutDate: false,
  withoutTime: false,
  dateFormat: 'yyyy-MM-dd HH:mm:ss',
  timeFormat: 'HH:mm',
  timeCaption: defineMessage({
    "id": "s4Xrs9",
    "defaultMessage": [{
      "type": 0,
      "value": "Time"
    }]
  }),
  timeIntervals: 15,
  className: null,
  onChange: null
};
var DateTimeField = function DateTimeField(_ref) {
  var name = _ref.name,
    value = _ref.value,
    errors = _ref.errors,
    size = _ref.size,
    required = _ref.required,
    disabled = _ref.disabled,
    placeholder = _ref.placeholder,
    fnsFormat = _ref.format,
    withoutDate = _ref.withoutDate,
    withoutTime = _ref.withoutTime,
    dateFormat = _ref.dateFormat,
    timeFormat = _ref.timeFormat,
    timeCaption = _ref.timeCaption,
    timeIntervals = _ref.timeIntervals,
    onChange = _ref.onChange,
    className = _ref.className;
  var _useIntl = useIntl(),
    locale = _useIntl.locale,
    formatMessage = _useIntl.formatMessage;
  // The internal value of this field must be a Date object
  var parseDate = useCallback(function (date) {
    if (date instanceof Date) {
      return date;
    }
    if (fnsFormat) {
      return parse(date, fnsFormat, new Date());
    }
    return parseISO(date);
  }, [fnsFormat]);
  var formatDate = useCallback(function (date) {
    if (fnsFormat !== null) {
      return format(date, fnsFormat);
    }
    return formatISO(date);
  }, [fnsFormat]);
  var _useState = useState(value !== null ? parseDate(value) : null),
    _useState2 = _slicedToArray(_useState, 2),
    dateValue = _useState2[0],
    setDateValue = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    loadedLocale = _useState4[0],
    setLoadedLocale = _useState4[1];
  var onDateChange = useCallback(function (newDate) {
    setDateValue(newDate);
    var strValue = newDate !== null ? formatDate(newDate, dateFormat) : null;
    if (onChange !== null) {
      onChange(strValue);
    }
  }, [setDateValue, dateFormat, onChange]);
  useEffect(function () {
    var localeName = "".concat(locale, "-CA");
    var loader = locale === 'fr' ? loadPackage('date-fns/locale/fr-CA', function () {
      return import('date-fns/locale/fr-CA');
    }) : loadPackage('date-fns/locale/en-CA', function () {
      return import('date-fns/locale/en-CA');
    });
    loader.then(function (_ref2) {
      var localePackage = _ref2["default"];
      registerLocale(localeName, localePackage);
      setLoadedLocale(localeName);
    });
  }, [locale, setLoadedLocale]);
  useEffect(function () {
    setDateValue(value !== null ? parseDate(value) : null);
  }, [value, setDateValue, parseDate]);
  var TextFieldComponent = /*#__PURE__*/React.createElement(TextField, {
    name: name,
    size: size,
    errors: errors,
    required: required,
    className: className,
    disabled: disabled,
    nativeOnChange: true
  });
  return /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement(FinalDatePicker, {
    selected: dateValue,
    onChange: onDateChange,
    showTimeSelect: !withoutTime,
    showTimeSelectOnly: withoutDate,
    disabled: disabled,
    customInput: TextFieldComponent,
    placeholderText: isMessage(placeholder) ? formatMessage(placeholder) : placeholder,
    dateFormat: dateFormat,
    locale: loadedLocale,
    timeFormat: timeFormat,
    timeCaption: isMessage(timeCaption) ? formatMessage(timeCaption) : timeCaption,
    timeIntervals: timeIntervals,
    popperClassName: styles.popper
  }));
};
DateTimeField.propTypes = propTypes$2;
DateTimeField.defaultProps = defaultProps$2;

var _excluded$1 = ["dateFormat"];
var propTypes$1 = {
  dateFormat: PropTypes.string
};
var defaultProps$1 = {
  dateFormat: 'HH:mm'
};
var TimeField$1 = function TimeField(_ref) {
  var dateFormat = _ref.dateFormat,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return /*#__PURE__*/React.createElement(DateTimeField, Object.assign({}, props, {
    withoutDate: true,
    dateFormat: dateFormat
  }));
};
TimeField$1.propTypes = propTypes$1;
TimeField$1.defaultProps = defaultProps$1;

var _excluded = ["dateFormat"];
var propTypes = {
  dateFormat: PropTypes.string
};
var defaultProps = {
  dateFormat: 'yyyy-MM-dd'
};
var TimeField = function TimeField(_ref) {
  var dateFormat = _ref.dateFormat,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(DateTimeField, Object.assign({}, props, {
    withoutTime: true,
    dateFormat: dateFormat
  }));
};
TimeField.propTypes = propTypes;
TimeField.defaultProps = defaultProps;

export { TimeField as Date, DateTimeField as DateTime, TimeField$1 as Time, TimeField as default, definition };
